import { dailyScoreLedgerRepository } from '../db/database';
import type { DailyScoreLedger, IsoDate } from '../models/types';
import { setDelegatedClick } from '../ui/delegatedClick';
import {
  filterByRange,
  renderChart,
  resolveRange,
  RANGE_OPTIONS,
  type ChartMetric,
  type RangeKey,
} from '../ui/historyChart';
import { renderIcon } from '../ui/icons';
import type { RouteId } from '../ui/tabBar';
import { todayIso } from '../utils/date';

/**
 * Detaylı Geçmiş — Profil > Detaylı Geçmiş (Faz 16).
 *
 * `dailyScoreLedger`'daki kapanmış gün kayıtlarını gösterir. Bu
 * kayıtlar §7.2 gereği DEĞİŞTİRİLEMEZ; bu ekran yalnızca okur, hiçbir
 * şey yazmaz.
 *
 * Ekran iki bölümden oluşur (§7.9):
 * 1. **Grafik kartı** — kendi tarih aralığı seçicisiyle, genel gidişatı
 *    gösterir. Çizimi `ui/historyChart.ts` yapar.
 * 2. **Gün gün döküm** — kendi üç aylık dönem seçicisiyle, tek tek
 *    günleri listeler.
 *
 * İki seçici bilinçli olarak BAĞIMSIZDIR; gerekçesi aşağıda, durum
 * değişkenlerinin yanında yazılı.
 *
 * Dışa aktarma (CSV/PDF) hâlâ kapsam dışı — CLAUDE.md §8.
 */

const MONTH_NAMES = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

/** Pazar = 0 olan JS sırasına göre kısa gün adları. */
const WEEKDAY_SHORT = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

/** Üç aylık dönem etiketleri — dizin = çeyrek numarası (0–3). */
const QUARTER_LABELS = ['Oca–Mar', 'Nis–Haz', 'Tem–Eyl', 'Eki–Ara'];

/**
 * Seçili üç aylık dönem (`yyyy-Q` biçiminde, örn. `2026-2`).
 *
 * Modül düzeyinde tutuluyor çünkü ekran her dönem değişiminde kendini
 * yeniden çiziyor. `null` = "en yeni dönem" — veri değiştiğinde
 * kendiliğinden doğru döneme düşsün diye sabit bir değer yazmıyoruz.
 */
let selectedPeriod: string | null = null;

/* ------------------------------------------------------------------
   Grafiğin durumu

   DİKKAT — grafiğin tarih aralığı ile listenin çeyrek seçimi BİRBİRİNDEN
   BAĞIMSIZDIR (kullanıcı kararı). İkisi ayrı soruya cevap veriyor:
   grafik "genel gidişat nasıl?", liste "şu ayda tam olarak ne oldu?".
   Bu yüzden ekranda iki ayrı seçici var ve her biri kendi başlığının
   altında duruyor — hangisinin neyi değiştirdiği yerinden anlaşılsın.
   ------------------------------------------------------------------ */

let chartMetric: ChartMetric = 'bar';
/** Açılışta 90 gün: 30 gün fazla dar, "tümü" ilk bakışta fazla yoğun. */
let chartRange: RangeKey = '90';
/** `chartRange === 'custom'` iken iki tarih alanının değeri. */
let customStart: IsoDate | null = null;
let customEnd: IsoDate | null = null;

/**
 * Tarihi `Date` nesnesine çevirir.
 *
 * Öğlen 12:00 çapası, `utils/date.ts`'teki ile aynı sebepten: yaz saati
 * geçişlerinde gece yarısına sabitlenmiş bir tarih bir önceki/sonraki
 * güne kayabilir.
 */
function toDate(date: IsoDate): Date {
  return new Date(`${date}T12:00:00`);
}

/** Değişimi işaretiyle yazar: `+18`, `-4`, `0`. */
function formatDelta(delta: number): string {
  return delta > 0 ? `+${delta}` : String(delta);
}

/** Değişimin rengini belirleyen sınıf eki. */
function deltaTone(delta: number): string {
  if (delta > 0) return 'history-delta--up';
  if (delta < 0) return 'history-delta--down';
  return 'history-delta--flat';
}

/* ==================================================================
   Özet kartı
   ================================================================== */

/**
 * Tüm geçmişin üç satırlık özeti.
 *
 * Kullanıcı ekrana girdiğinde önce "genel olarak nasıl gidiyorum?"
 * sorusunun cevabını görsün; gün gün dökümü altta duruyor.
 */
function renderSummary(entries: DailyScoreLedger[]): string {
  const streakDays = entries.filter((entry) => entry.wasStreakEligible).length;
  const bestBar = entries.reduce((max, entry) => Math.max(max, entry.barValueAfter), 0);

  return `
    <section class="history-summary">
      <div class="history-summary__item">
        <p class="history-summary__value">${entries.length}</p>
        <p class="history-summary__label">kayıtlı gün</p>
      </div>
      <div class="history-summary__item">
        <p class="history-summary__value">${streakDays}</p>
        <p class="history-summary__label">seriye sayıldı</p>
      </div>
      <div class="history-summary__item">
        <p class="history-summary__value">${bestBar}</p>
        <p class="history-summary__label">en yüksek bar</p>
      </div>
    </section>
  `;
}

/* ==================================================================
   Gün satırı
   ================================================================== */

/**
 * Tek bir günün satırı.
 *
 * Soldaki takvim çipi günü ve haftanın gününü taşır; sağda o günün
 * puan değişimi ve değişim sonrası bar değeri durur. Rozetler yalnızca
 * ilgili olduklarında görünür — her satırda duran rozet, göz onları
 * taramayı bıraktığı için bilgi taşımaz olurdu.
 */
function renderDayRow(entry: DailyScoreLedger): string {
  const date = toDate(entry.date);

  const badges = [
    entry.wasStreakEligible
      ? `<span class="history-badge history-badge--streak" title="Bu gün seriye sayıldı">
           ${renderIcon('flame', { size: 12 })}
         </span>`
      : '',
    entry.wasVacationDay
      ? `<span class="history-badge history-badge--vacation" title="Tatil günü">
           ${renderIcon('gift', { size: 12 })}
         </span>`
      : '',
    // Ödülle satın alınan gün (§7.6). Tatille aynı etkiyi yapar ama
    // kaynağı farklı olduğu için ayrı rozetle gösteriliyor.
    entry.wasRewardDay
      ? `<span class="history-badge history-badge--reward" title="Ödülle alınan gün">
           ${renderIcon('trophy', { size: 12 })}
         </span>`
      : '',
  ].join('');

  return `
    <li class="history-row">
      <span class="history-chip">
        <span class="history-chip__day">${date.getDate()}</span>
        <span class="history-chip__weekday">${WEEKDAY_SHORT[date.getDay()] ?? ''}</span>
      </span>

      <span class="history-row__badges">${badges}</span>

      <span class="history-row__numbers">
        <span class="history-delta ${deltaTone(entry.finalDelta)}">
          ${formatDelta(entry.finalDelta)}
        </span>
        <span class="history-row__bar">${entry.barValueAfter}</span>
      </span>
    </li>
  `;
}

/* ==================================================================
   Grafik kartı
   ================================================================== */

/** Metrik sekmeleri — grafiğin neyi çizdiğini seçer. */
const METRIC_TABS: readonly { key: ChartMetric; label: string }[] = [
  { key: 'bar', label: 'Başarı Barı' },
  { key: 'delta', label: 'Günlük Değişim' },
];

/**
 * "Özel" aralık seçiliyken görünen iki tarih alanı.
 *
 * `min`/`max`, verinin gerçek sınırlarına bağlanıyor: iOS'un kendi
 * tarih seçicisi bu sayede kayıt olmayan günleri hiç göstermez ve
 * kullanıcı boş bir aralık seçmek için uğraşmaz.
 */
function renderCustomRange(bounds: { first: IsoDate; last: IsoDate }): string {
  return `
    <div class="custom-range">
      <label class="custom-range__field">
        <span class="custom-range__label">Başlangıç</span>
        <input type="date" class="custom-range__input" data-custom-start
               value="${customStart ?? bounds.first}"
               min="${bounds.first}" max="${bounds.last}" />
      </label>
      <label class="custom-range__field">
        <span class="custom-range__label">Bitiş</span>
        <input type="date" class="custom-range__input" data-custom-end
               value="${customEnd ?? bounds.last}"
               min="${bounds.first}" max="${bounds.last}" />
      </label>
    </div>
  `;
}

/**
 * Grafik kartının tamamı: başlık, metrik sekmeleri, aralık seçici,
 * SVG ve özet satırı.
 *
 * Kart kendi başına çiziliyor (sayfanın tamamı değil): sekmeye ya da
 * aralığa dokunmak sayfayı baştan kurmasın, kullanıcı listede nereye
 * kaydırdıysa orada kalsın diye.
 */
function renderChartCard(entries: readonly DailyScoreLedger[], today: IsoDate): string {
  const first = entries[0]?.date;
  const last = entries[entries.length - 1]?.date;
  if (!first || !last) return '';

  const range = resolveRange(chartRange, { start: customStart, end: customEnd }, entries, today);
  // `resolveRange` yalnızca kayıt yokken null döner; yukarıda zaten
  // elendi, ama tip güvenliği için kontrol duruyor.
  const visible = range ? filterByRange(entries, range) : [];
  const chart = range
    ? renderChart(visible, chartMetric, range)
    : { svg: '', summary: '' };

  const tabs = METRIC_TABS.map(
    (tab) => `
      <button type="button"
              class="chart-tab ${tab.key === chartMetric ? 'chart-tab--active' : ''}"
              data-chart-metric="${tab.key}">
        ${tab.label}
      </button>
    `,
  ).join('');

  const pills = RANGE_OPTIONS.map(
    (option) => `
      <button type="button"
              class="period-pill ${option.key === chartRange ? 'period-pill--active' : ''}"
              data-chart-range="${option.key}">
        ${option.label}
      </button>
    `,
  ).join('');

  return `
    <section class="chart-card">
      <div class="chart-tabs" role="tablist">${tabs}</div>
      <div class="period-picker period-picker--inline" role="tablist">${pills}</div>
      ${chartRange === 'custom' ? renderCustomRange({ first, last }) : ''}
      ${chart.svg}
      ${chart.summary ? `<p class="chart__summary">${chart.summary}</p>` : ''}
    </section>
  `;
}

/* ==================================================================
   Üç aylık dönemler

   Uzun süre kullanıldığında liste yüzlerce satıra çıkar; hepsini alt
   alta dökmek ekranı kullanılmaz hâle getirirdi. Bunun yerine kayıtlar
   TAKVİM ÇEYREKLERİNE bölünüp üstten seçiliyor.

   Neden çeyrek (Oca–Mar…), "son 3 ay" gibi kayan bir pencere değil:
   çeyreklerin sınırları sabittir, dolayısıyla bir dönemin etiketi ve
   içeriği yarın da aynı kalır — kayan pencerede aynı gün her gün
   başka bir gruba düşerdi.
   ================================================================== */

interface Period {
  /** `yyyy-Q` — sıralama ve seçim anahtarı. */
  key: string;
  label: string;
  entries: DailyScoreLedger[];
}

/** Bir tarihin ait olduğu çeyreğin anahtarı. */
function periodKeyOf(date: IsoDate): string {
  const month = Number(date.slice(5, 7));
  return `${date.slice(0, 4)}-${Math.floor((month - 1) / 3)}`;
}

/** Kayıtları çeyreklere böler, en yeni dönem başta olacak şekilde. */
function groupByPeriod(entries: DailyScoreLedger[]): Period[] {
  const groups = new Map<string, Period>();

  for (const entry of entries) {
    const key = periodKeyOf(entry.date);
    const existing = groups.get(key);
    if (existing) {
      existing.entries.push(entry);
      continue;
    }

    const [year = '', quarter = '0'] = key.split('-');
    groups.set(key, {
      key,
      label: `${QUARTER_LABELS[Number(quarter)] ?? ''} ${year}`,
      entries: [entry],
    });
  }

  // `yyyy-Q` metin olarak sıralandığında kronolojik sıralanır.
  return [...groups.values()].sort((a, b) => (a.key < b.key ? 1 : -1));
}

/** Dönem seçici — yatay kaydırılabilir pill şeridi. */
function renderPeriodPicker(periods: Period[], activeKey: string): string {
  if (periods.length <= 1) return '';

  const pills = periods
    .map(
      (period) => `
        <button type="button"
                class="period-pill ${period.key === activeKey ? 'period-pill--active' : ''}"
                data-period="${period.key}">
          ${period.label}
        </button>
      `,
    )
    .join('');

  return `<div class="period-picker" role="tablist">${pills}</div>`;
}

/* ==================================================================
   Ay grupları
   ================================================================== */

interface MonthGroup {
  /** `yyyy-mm` — gruplama anahtarı ve sıralama ölçütü. */
  key: string;
  label: string;
  entries: DailyScoreLedger[];
  /** Ayın toplam puan değişimi. */
  net: number;
}

/**
 * Kayıtları aya göre gruplar, en yeni ay en üstte olacak şekilde.
 *
 * Gruplama, uzun bir listeyi tararken kullanıcıya sabit bir tutamak
 * verir: "Eylül nasıl geçti?" sorusu tek bakışta cevaplanabilir olur.
 */
function groupByMonth(entries: DailyScoreLedger[]): MonthGroup[] {
  const groups = new Map<string, MonthGroup>();

  for (const entry of entries) {
    const key = entry.date.slice(0, 7);
    const existing = groups.get(key);

    if (existing) {
      existing.entries.push(entry);
      existing.net += entry.finalDelta;
      continue;
    }

    const date = toDate(entry.date);
    groups.set(key, {
      key,
      label: `${MONTH_NAMES[date.getMonth()] ?? ''} ${date.getFullYear()}`,
      entries: [entry],
      net: entry.finalDelta,
    });
  }

  // `yyyy-mm` metin olarak sıralandığında kronolojik sıralanır; ters
  // çevirerek en yeni ayı başa alıyoruz.
  return [...groups.values()].sort((a, b) => (a.key < b.key ? 1 : -1));
}

function renderMonth(group: MonthGroup): string {
  return `
    <section class="history-month">
      <header class="history-month__header">
        <h2 class="history-month__title">${group.label}</h2>
        <span class="history-month__net ${deltaTone(group.net)}">
          ${formatDelta(group.net)}
        </span>
      </header>
      <ul class="history-list" role="list">
        ${group.entries.map(renderDayRow).join('')}
      </ul>
    </section>
  `;
}

/* ==================================================================
   Ekran
   ================================================================== */

export async function renderHistoryPage(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
): Promise<void> {
  // Depo kayıtları eskiden yeniye verir. Grafik bu sırayı olduğu gibi
  // kullanır (soldan sağa zaman akışı); liste ise ters çevrilmiş hâlini
  // kullanır, çünkü geçmişte en çok merak edilen son günlerdir.
  const ascending = await dailyScoreLedgerRepository.list();
  const entries = [...ascending].reverse();
  const today = todayIso();
  const periods = groupByPeriod(entries);

  // Seçili dönem artık yoksa (veri silindi ya da hiç seçilmedi) en
  // yeni döneme düşüyoruz.
  const active =
    periods.find((period) => period.key === selectedPeriod) ?? periods[0];

  const body = active
    ? `<div data-chart-host>${renderChartCard(ascending, today)}</div>
       <h2 class="history-section-title">Gün gün döküm</h2>
       ${renderPeriodPicker(periods, active.key)}
       ${renderSummary(active.entries)}
       ${groupByMonth(active.entries).map(renderMonth).join('')}`
    : `<div class="placeholder">
         <p class="placeholder__title">Henüz kayıt yok</p>
         <p class="placeholder__hint">
           İlk kayıt, bugün kapandığında oluşur. Uygulamayı yarın açtığında
           burada görünür.
         </p>
       </div>`;

  container.innerHTML = `
    <header class="detail-header">
      <button type="button" class="back-button" data-back aria-label="Profile dön">
        <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true" focusable="false">
          <path d="M7 1L1 7l6 6" stroke="currentColor" stroke-width="2" fill="none"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <h1 class="detail-header__title">Detaylı Geçmiş</h1>
    </header>
    ${body}
  `;

  bindCustomRangeInputs(container, ascending, today);
  const chartHost = container.querySelector<HTMLElement>('[data-chart-host]');
  if (chartHost) revealActiveRangePill(chartHost);

  // Dönem değiştirmek sayfayı yeniden çizdiği için `setDelegatedClick`
  // şart: doğrudan `addEventListener` kullanılsaydı her çizimde bir
  // dinleyici daha birikir ve tek dokunuş birden çok kez işlenirdi.
  setDelegatedClick('history', container, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-back]')) {
      void navigate('profile');
      return;
    }

    // --- Grafik: metrik sekmesi -------------------------------------
    const metricTab = target.closest<HTMLElement>('[data-chart-metric]');
    if (metricTab) {
      chartMetric = metricTab.dataset['chartMetric'] === 'delta' ? 'delta' : 'bar';
      refreshChart(container, ascending, today);
      return;
    }

    // --- Grafik: tarih aralığı --------------------------------------
    const rangePill = target.closest<HTMLElement>('[data-chart-range]');
    if (rangePill) {
      // Kullanıcı özel aralıkta tarih girip sonra hazır bir aralığa
      // geçerse, girdiği tarihler kaybolmasın diye önce okuyoruz.
      captureCustomRange(container);
      chartRange = (rangePill.dataset['chartRange'] ?? '90') as RangeKey;
      refreshChart(container, ascending, today);
      return;
    }

    // --- Liste: üç aylık dönem --------------------------------------
    const pill = target.closest<HTMLElement>('[data-period]');
    if (pill) {
      selectedPeriod = pill.dataset['period'] ?? null;
      void (async () => {
        await renderHistoryPage(container, navigate);
        // Dönem değişince listenin başına dön: kullanıcı önceki
        // dönemde nerede kaldıysa oradan devam etmemeli.
        container.scrollTop = 0;
      })();
    }
  });
}

/**
 * Yalnızca grafik kartını yeniden çizer.
 *
 * Sayfanın tamamını çizmiyoruz: kullanıcı listeyi aşağı kaydırmış
 * olabilir ve grafik sekmesine dokunmak onu en başa fırlatırdı.
 */
function refreshChart(
  container: HTMLElement,
  ascending: readonly DailyScoreLedger[],
  today: IsoDate,
): void {
  const host = container.querySelector<HTMLElement>('[data-chart-host]');
  if (!host) return;
  host.innerHTML = renderChartCard(ascending, today);
  bindCustomRangeInputs(container, ascending, today);
  revealActiveRangePill(host);
}

/**
 * Seçili aralık pill'ini yatay şeridin ortasına kaydırır.
 *
 * Kart yeniden çizildiğinde şerit en başa sarılıyor; "Tümü" gibi sağdaki
 * bir aralığı seçtiğinde kullanıcı kendi seçimini ekran dışında
 * bırakılmış buluyordu. `scrollIntoView` yerine `scrollLeft`
 * kullanıyoruz: o yöntem sayfayı dikey olarak da kaydırabiliyor.
 */
function revealActiveRangePill(host: HTMLElement): void {
  const strip = host.querySelector<HTMLElement>('.period-picker--inline');
  const active = strip?.querySelector<HTMLElement>('.period-pill--active');
  if (!strip || !active) return;

  strip.scrollLeft = Math.max(0, active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2);
}

/** Ekrandaki iki tarih alanının değerini modül durumuna alır. */
function captureCustomRange(container: HTMLElement): void {
  const start = container.querySelector<HTMLInputElement>('[data-custom-start]');
  const end = container.querySelector<HTMLInputElement>('[data-custom-end]');
  if (start?.value) customStart = start.value;
  if (end?.value) customEnd = end.value;
}

/**
 * Tarih alanlarını dinler.
 *
 * `setDelegatedClick` burada işe yaramaz — tarih alanı `click` değil
 * `change` olayı üretir (iOS'ta seçici kapandığında). Dinleyiciyi
 * doğrudan alanların kendisine bağlamak güvenli: grafik her
 * çizildiğinde bu düğümler yenisiyle değişiyor, eskiler dinleyicileriyle
 * birlikte çöpe gidiyor. Dolayısıyla dinleyici birikmesi olmaz.
 */
function bindCustomRangeInputs(
  container: HTMLElement,
  ascending: readonly DailyScoreLedger[],
  today: IsoDate,
): void {
  const inputs = container.querySelectorAll<HTMLInputElement>(
    '[data-custom-start], [data-custom-end]',
  );

  for (const input of inputs) {
    input.addEventListener('change', () => {
      captureCustomRange(container);
      refreshChart(container, ascending, today);
    });
  }
}

/**
 * Ekrandan çıkarken dönem seçimini sıfırlar.
 *
 * Seçim modül düzeyinde tutuluyor; temizlenmezse kullanıcı eski bir
 * çeyreğe bakıp çıktıktan sonra ekranı tekrar açtığında yine o eski
 * dönemde açılırdı — beklenen davranış en yeni döneme dönmek.
 */
export function resetHistoryScreenState(): void {
  selectedPeriod = null;
  chartMetric = 'bar';
  chartRange = '90';
  customStart = null;
  customEnd = null;
}
